import React, { useEffect, useState } from 'react';
import { getGallery, GalleryFilters } from '../api/gallery.api';
import { Gallery } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

const GalleryPage: React.FC = () => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchGalleries = async () => {
      setLoading(true);
      try {
        const filters: GalleryFilters = {
          page: currentPage,
          limit: 12,
        };
        const response = await getGallery(filters);
        
        console.log('GalleryPage - getGallery response:', response);
        console.log('GalleryPage - response.data:', response?.data);
        console.log('GalleryPage - response.data.galleries:', response?.data?.galleries);
        
        if (response && response.data) {
          const galleriesList = response.data.galleries || [];
          console.log('GalleryPage - Setting galleries:', galleriesList.length, 'items');
          setGalleries(galleriesList);
          setTotalPages(response.data.totalPages || 1);
          setTotal(response.data.total || 0);
        } else {
          console.warn('GalleryPage - No response data');
          setGalleries([]);
          setTotalPages(1);
          setTotal(0);
        }
      } catch (err: any) {
        console.error('GalleryPage - Error fetching galleries:', err);
        console.error('GalleryPage - Error response:', err.response);
        console.error('GalleryPage - Error data:', err.response?.data);
        console.error('GalleryPage - Error status:', err.response?.status);
        
        // Show user-friendly error message
        if (err.response?.status === 401) {
          console.error('GalleryPage - Unauthorized - user may need to login');
        } else if (err.response?.status === 403) {
          console.error('GalleryPage - Forbidden - user may not have access');
        } else if (err.response?.status === 404) {
          console.error('GalleryPage - Not found - endpoint may not exist');
        }
        
        setGalleries([]);
        setTotalPages(1);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleries();
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && galleries.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container py-5">
      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4 gap-3">
        <div>
          <h1 className="h3 section-title">Gallery &nbsp;</h1>
          <p className="text-muted-soft mb-0">Browse our collection of images</p>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : galleries.length === 0 ? (
        <div className="alert alert-secondary mb-0">
          No gallery images available.
        </div>
      ) : (
        <>
          <div className="row g-4 mb-4">
            {galleries.map((gallery) => (
              <div key={gallery.id} className="col-12 col-md-6 col-lg-4">
                <div className="card card-surface h-100">
                  <div 
                    className="position-relative" 
                    style={{ 
                      minHeight: '200px',
                      maxHeight: '400px',
                      aspectRatio: '4/3',
                      overflow: 'hidden',
                      backgroundColor: '#0a0a0a',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <img
                      src={gallery.imageUrl}
                      alt={gallery.title}
                      className="card-img-top"
                      style={{
                        width: '100%',
                        height: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease',
                      }}
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="250"%3E%3Crect fill="%230a0a0a" width="400" height="250"/%3E%3Ctext fill="%23ffffff" font-family="Arial" font-size="18" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage Not Available%3C/text%3E%3C/svg%3E';
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    />
                  </div>
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title mb-2" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                      {gallery.title}
                    </h5>
                    {gallery.description && (
                      <p className="text-muted-soft flex-grow-1 small" style={{ fontSize: 'clamp(0.875rem, 2vw, 1rem)' }}>
                        {gallery.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="Page navigation" className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                    Previous
                  </button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                    Next
                  </button>
                </li>
              </ul>
              <div className="text-center mt-2 text-muted-soft small">
                Showing {galleries.length} of {total} image{total !== 1 ? 's' : ''}
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default GalleryPage;
